import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  Alert,
  Snackbar,
  Dialog,
} from "@mui/material";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/de";
import "moment/locale/es";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  resolveMobileSessionNavPath,
  ensureCoachRoleForLiveTrainingEntry,
} from "../utils/liveTrainingNav";
import { persistLiveTrainingSession } from "../hooks/useLiveTrainingRedirect";
import { formatTrainingSessionLabel } from "../utils/formatTrainingSessionLabel";
import { useTourDemoActive } from "../tour/useTourDemoActive";
import { demoSessionsForDate } from "../tour/tourDemoFixtures";
import { CLOSE_TOUR_TRANSIENT_UI_EVENT } from "../tour/tourEvents";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import sessionIcon from "./assets/session-clipboard-icon.png";
import tennis2 from "./assets/timerInProgress.png";
import Manualsession from "./Manualsession";
import SeasonalSession from "./SeasonalSession";
import DuplicateSessionDialog from "./DuplicateSessionDialog";
import TrainingSessionResultsDialog from "./TrainingSessionResultsDialog";
import ExerciseResultDialog from "./ExerciseResultDialog";
import PlayerPresenceDialog from "./PlayerPresenceDialog";
import { FinalizedExerciseSeatingDialog } from "../pages/coach/FinalizedExerciseSeatingDialog";
import { ExerciseCardHrSummary } from "./ExerciseCardHrSummary";
import "./PlayerSessionResultsDialog.css";
import "./AddSessionDialog.css";
import {
  createsession,
  createSeasonalSessions,
  sessionlist,
  deleteTrainingSession,
  duplicateTrainingSession,
  playerpresent,
  grpplayerlist,
  sessionexlist,
  exdetails,
  tablelist,
  grpuserlist,
  mobileGroupPlayers,
  mobileSessionDetails,
  groupdetails,
} from "./funtionsBD/Mainservice";
import {
  buildPlayerIndexes,
  fetchCoachSessionRoster,
  resolveClubIdCandidates,
  resolveTableGrId,
} from "../utils/sessionResultsPlayers";
import { usePermissions } from "../context/Permissioncontext";
import { isPlayerAccount, readActiveHomeRole, readStoredUser } from "../utils/homeRole";
import {
  userCanEditPlanningInCurrentView,
  userCanManageTrainingSessions,
  userCanManagePastTrainingSessions,
  userCanEditFinalizedExerciseSeating,
  resolveViewerPlayerIdFromUser,
} from "../utils/planningAccess";
import { isPlayerPresentInTrainingSession } from "../utils/trainingRoomAccess";
import { isSessionClosedForPresence } from "../utils/sessionPresenceEdit";

/** Resolve gr_id from home page group row (Sequelize JSON may use dataValues). */
function homeGroupRowGrId(g) {
  const d = g?.groupDetails;
  if (!d) return g?.gr_id || null;
  return (
    d.gr_id ||
    d.dataValues?.gr_id ||
    (typeof d.toJSON === "function" && d.toJSON()?.gr_id) ||
    g?.gr_id ||
    null
  );
}

function homeGroupRowName(g) {
  const d = g?.groupDetails;
  if (!d) return g?.gr_name || "";
  return d.gr_name || d.dataValues?.gr_name || g?.gr_name || "";
}

function isAutoHomeGroupRow(g) {
  const d = g?.groupDetails;
  const mode =
    d?.gr_traning_session_type ||
    d?.dataValues?.gr_traning_session_type ||
    g?.gr_traning_session_type;
  return mode === "auto";
}

/** Match a preferred group id inside a list (string/number safe). */
function findHomeGroupRowById(rows, preferredId) {
  if (preferredId == null || preferredId === "") return null;
  const want = String(preferredId);
  return (
    (rows || []).find((g) => String(homeGroupRowGrId(g) || "") === want) || null
  );
}

function readStoredGroupRowFromLocalStorage() {
  try {
    return JSON.parse(localStorage.getItem("grpinf") || "null");
  } catch {
    return null;
  }
}

/**
 * Default group for the add-session dialog: last used (selected / grpinf),
 * else the last selectable (non-auto) group in the list.
 */
function resolveDefaultAddSessionGroup({
  preferredRows = [],
  selectedgrp = null,
  eventSelectedgrp = null,
} = {}) {
  const selectable = (preferredRows || []).filter((g) => !isAutoHomeGroupRow(g));
  if (!selectable.length) return null;

  const candidates = [
    eventSelectedgrp,
    selectedgrp,
    readStoredGroupRowFromLocalStorage(),
  ];
  for (const candidate of candidates) {
    if (!candidate || isAutoHomeGroupRow(candidate)) continue;
    const matched = findHomeGroupRowById(selectable, homeGroupRowGrId(candidate));
    if (matched) return matched;
  }
  return selectable[selectable.length - 1];
}

export const SessionsList = ({
  setSelectedSession,
  selectedSession,
  selectedgrp,
  selectedDate,
  coachHomeGroups,
  aggregateCoachSessionsAcrossGroupsByDate = false,
  /** When false, coach group list is still loading — do not fall back to single-group fetch. */
  coachGroupsListReady = true,
  /** From Home: coach groups or club groups for “add session” when none selected */
  addSessionGroupOptions,
  onSessionAddGroupChosen,
  playerHomeMode = false,
}) => {
  const [addsession, setaddsession] = useState(false);
  const [type, settype] = useState();
  const [sessionTypeDialog, setSessionTypeDialog] = useState(false);
  const [selectedSessionType, setSelectedSessionType] = useState(null); // "seasonal" or "spontaneous"
  const [success, setsuccess] = useState();
  const [error, seterror] = useState();
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(false);
  const [allSessions, setAllSessions] = useState([]); // Store all sessions before filtering
  const [sessionPlayerData, setSessionPlayerData] = useState({}); // Map of ts_id -> player data for current user
  const [sessionListLoading, setSessionListLoading] = useState(false);
  /** Matches sessionsFetchKey once the current list fetch has finished. */
  const [settledFetchKey, setSettledFetchKey] = useState(null);
  /** Bumps when a new session-list fetch starts; stale responses must not overwrite state. */
  const listFetchGenRef = useRef(0);
  const [duplicateDialogSession, setDuplicateDialogSession] = useState(null);
  const [dupCalendarDate, setDupCalendarDate] = useState(() => new Date());
  const [dupTime, setDupTime] = useState("");
  const [dupSubmitting, setDupSubmitting] = useState(false);
  /** Group chosen in the duplicate-session dialog when home has no usable selection (or auto mode). */
  const [dupDialogGroup, setDupDialogGroup] = useState(null);
  /** Group chosen in the add-session dialog when home has no usable selection (or auto mode). */
  const [addDialogGroup, setAddDialogGroup] = useState(null);

  const [playerSessionResultOpen, setPlayerSessionResultOpen] = useState(false);
  const [playerSelectedSessionForResults, setPlayerSelectedSessionForResults] = useState(null);
  const [presenceDialogOpen, setPresenceDialogOpen] = useState(false);
  const [presenceDialogSession, setPresenceDialogSession] = useState(null);
  const [playerExerciseResultOpen, setPlayerExerciseResultOpen] = useState(false);
  const [playerSelectedExerciseForResults, setPlayerSelectedExerciseForResults] = useState(null);
  const [playerExerciseResultInitialTab, setPlayerExerciseResultInitialTab] = useState("perf");
  const [seatingExercise, setSeatingExercise] = useState(null);

  useEffect(() => {
    const onOpenExerciseResult = (event) => {
      const { session, exercise, initialTab } = event.detail || {};
      if (!session?.ts_id || !exercise?.te_id) return;
      setPlayerSelectedSessionForResults(session);
      setPlayerSelectedExerciseForResults(exercise);
      setPlayerExerciseResultInitialTab(initialTab || "perf");
      setPlayerExerciseResultOpen(true);
    };
    window.addEventListener("smarttt-open-exercise-result", onOpenExerciseResult);
    return () => {
      window.removeEventListener("smarttt-open-exercise-result", onOpenExerciseResult);
    };
  }, []);

  const [sessionDetails, setSessionDetails] = useState({});

  const triggerHaptic = () => {
    if (navigator.vibrate) {
      try {
        navigator.vibrate(10);
      } catch (e) {
        // ignore
      }
    }
  };

  const { t, i18n } = useTranslation();
  const tourDemo = useTourDemoActive();
  const navigate = useNavigate();
  const closealert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  const user = readStoredUser();
  const role = user?.type_user_id;
  const homeRole = readActiveHomeRole(user);
  const isPlayerUi = playerHomeMode || isPlayerAccount(user, { homeRole });
  const rawLang = (i18n.language || localStorage.getItem("i18nextLng") || "en")
    .split("-")[0]
    .toLowerCase();
  const currentlng = rawLang === "gr" ? "de" : rawLang === "sp" ? "es" : rawLang;
  const { hasPermission, permissions } = usePermissions();
  const localeCode = currentlng || "en";
  const isCoachStaffUi =
    !isPlayerUi && userCanEditPlanningInCurrentView(user, permissions);
  const canManageSessions = userCanManageTrainingSessions(
    user,
    permissions,
    hasPermission
  );
  const canManagePastSessions = userCanManagePastTrainingSessions(user);
  const canEditFinalizedSeating = userCanEditFinalizedExerciseSeating(
    user,
    permissions
  );
  const canCorrectPresence =
    canManagePastSessions ||
    hasPermission("PLAYER_ADD_TO_SESSION") ||
    hasPermission("PLAYER_REMOVE_FROM_SESSION");

  const [coachFilterPlayers, setCoachFilterPlayers] = useState([]);
  const [coachPlayerByKey, setCoachPlayerByKey] = useState(new Map());
  const [coachPlayersLoading, setCoachPlayersLoading] = useState(false);
  const [coachSelectedPlayerId, setCoachSelectedPlayerId] = useState("all");

  const coachSelectedPlayer = useMemo(() => {
    if (!coachSelectedPlayerId || coachSelectedPlayerId === "all") return null;
    return coachPlayerByKey.get(String(coachSelectedPlayerId)) || null;
  }, [coachPlayerByKey, coachSelectedPlayerId]);

  const viewerParticipatedInSelectedSession = useMemo(() => {
    if (!isPlayerUi) return true;
    const tsId = playerSelectedSessionForResults?.ts_id;
    if (!tsId) return false;
    return isPlayerPresentInTrainingSession(sessionPlayerData[tsId]);
  }, [
    isPlayerUi,
    playerSelectedSessionForResults?.ts_id,
    sessionPlayerData,
  ]);

  // Coach results roster loading effect
  useEffect(() => {
    let cancelled = false;
    const tsId = playerSelectedSessionForResults?.ts_id;

    if (!tsId || isPlayerUi) {
      setCoachFilterPlayers([]);
      setCoachPlayerByKey(new Map());
      setCoachPlayersLoading(false);
      return;
    }

    const run = async () => {
      setCoachPlayersLoading(true);
      const grp = resolveGroupForSession(playerSelectedSessionForResults);
      const initialGrId = resolveTableGrId(playerSelectedSessionForResults, null, grp) || null;

      try {
        const clubCandidates = resolveClubIdCandidates(user, playerSelectedSessionForResults, grp);
        const { rosterRaw, grId } = await fetchCoachSessionRoster({
          tsId,
          grId: initialGrId,
          clubCandidates,
          sessionexlist,
          tablelist,
          mobileSessionDetails,
          mobileGroupPlayers,
          grpplayerlist,
          grpuserlist,
          groupdetails,
          exdetails,
        });

        if (cancelled) return;
        
        const { byKey, list } = buildPlayerIndexes(rosterRaw);
        setCoachFilterPlayers(list);
        setCoachPlayerByKey(byKey);
        if (list.length > 0) {
          setCoachSelectedPlayerId(list[0]._filterKey || list[0].player_id || list[0].user_id || "all");
        } else {
          setCoachSelectedPlayerId("all");
        }
      } catch (err) {
        console.error("Error loading coach session roster in SessionsList:", err);
        if (!cancelled) {
          setCoachFilterPlayers([]);
          setCoachPlayerByKey(new Map());
        }
      } finally {
        if (!cancelled) setCoachPlayersLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [playerSelectedSessionForResults?.ts_id, isPlayerUi]);

  const coachHomeRows = useMemo(
    () => (Array.isArray(coachHomeGroups) ? coachHomeGroups : []),
    [coachHomeGroups]
  );

  const rawGroupsForPicker = useMemo(() => {
    if (Array.isArray(addSessionGroupOptions) && addSessionGroupOptions.length > 0) {
      return addSessionGroupOptions;
    }
    return coachHomeRows;
  }, [addSessionGroupOptions, coachHomeRows]);

  const selectableGroups = useMemo(
    () => rawGroupsForPicker.filter((g) => !isAutoHomeGroupRow(g)),
    [rawGroupsForPicker]
  );

  const needsGroupPicker = useMemo(() => {
    if (!selectableGroups.length) return false;
    if (!homeGroupRowGrId(selectedgrp) || isAutoHomeGroupRow(selectedgrp)) {
      return true;
    }
    return false;
  }, [selectableGroups.length, selectedgrp]);

  const sessionCreationGroup = useMemo(() => {
    if (homeGroupRowGrId(addDialogGroup) && !isAutoHomeGroupRow(addDialogGroup)) {
      return addDialogGroup;
    }
    if (homeGroupRowGrId(selectedgrp) && !isAutoHomeGroupRow(selectedgrp)) {
      return selectedgrp;
    }
    return null;
  }, [addDialogGroup, selectedgrp]);

  const selectedgrpRef = useRef(selectedgrp);
  selectedgrpRef.current = selectedgrp;
  const selectableGroupsRef = useRef(selectableGroups);
  selectableGroupsRef.current = selectableGroups;
  const rawGroupsForPickerRef = useRef(rawGroupsForPicker);
  rawGroupsForPickerRef.current = rawGroupsForPicker;
  const addSessionGroupOptionsRef = useRef(addSessionGroupOptions);
  addSessionGroupOptionsRef.current = addSessionGroupOptions;
  const onSessionAddGroupChosenRef = useRef(onSessionAddGroupChosen);
  onSessionAddGroupChosenRef.current = onSessionAddGroupChosen;

  const sessionTypeActionsDisabled =
    needsGroupPicker && !homeGroupRowGrId(sessionCreationGroup);

  const aggregateCoachSessionsByDate =
    aggregateCoachSessionsAcrossGroupsByDate === true &&
    coachHomeRows.length > 0 &&
    !!selectedDate;

  /** Coach multi-group mode but home has not finished loading group rows — must not call single-group fetch. */
  const waitingForCoachGroupList =
    aggregateCoachSessionsAcrossGroupsByDate === true &&
    !!selectedDate &&
    coachGroupsListReady === false;

  const sessionsFetchKey = useMemo(() => {
    const dateKey = selectedDate
      ? moment(selectedDate).format("YYYY-MM-DD")
      : "";
    if (!dateKey) return null;
    if (waitingForCoachGroupList) return null;
    if (aggregateCoachSessionsByDate) {
      const ids = coachHomeRows
        .map(homeGroupRowGrId)
        .filter(Boolean)
        .sort()
        .join(",");
      // Player home: still fetch the day when group list is empty so personal bookings appear.
      if (ids) return `coach-agg:${ids}:${dateKey}`;
      if (playerHomeMode) return `player-owned:${dateKey}`;
      return null;
    }
    const grId = selectedgrp?.groupDetails?.gr_id;
    return grId ? `grp:${grId}:${dateKey}` : null;
  }, [
    waitingForCoachGroupList,
    aggregateCoachSessionsByDate,
    coachHomeRows,
    selectedgrp?.groupDetails?.gr_id,
    selectedDate,
    playerHomeMode,
  ]);

  const filterSessionsByDate = useCallback(
    (list) => {
      if (!selectedDate) return list;
      return list.filter((session) => {
        if (!session.ts_date) return false;
        const sessionDate = moment(session.ts_date).startOf("day");
        const selected = moment(selectedDate).startOf("day");
        return sessionDate.isSame(selected, "day");
      });
    },
    [selectedDate]
  );

  const filteredSessions = useMemo(
    () => filterSessionsByDate(allSessions),
    [allSessions, filterSessionsByDate]
  );

  const sessions = useMemo(() => {
    if (!selectedSession?.ts_id && selectedSession?.id == null) {
      return filteredSessions;
    }
    return filteredSessions.map((session) => ({
      ...session,
      active:
        (!!session.ts_id && session.ts_id === selectedSession.ts_id) ||
        (session.id != null && session.id === selectedSession.id),
    }));
  }, [filteredSessions, selectedSession]);

  const sessionsDataPending =
    !!selectedDate &&
    (waitingForCoachGroupList ||
      sessionListLoading ||
      (sessionsFetchKey != null && settledFetchKey !== sessionsFetchKey));

  useEffect(() => {
    if (!sessions || sessions.length === 0) return;

    let active = true;
    async function loadAllSessionDetails() {
      for (const session of sessions) {
        if (!session.ts_id || sessionDetails[session.ts_id]) continue;
        try {
          const res = await sessionexlist({ ts_id: session.ts_id });
          const rawList = res?.data?.data || [];

          const count = rawList.length;
          const duration = rawList.reduce((sum, ex) => sum + (ex.te_duration || 0), 0);

          let difficulty = "medium";
          const totalSequences = rawList.reduce((sum, ex) => sum + (ex.sequences?.length || 0), 0);
          if (duration > 60 || totalSequences > 8) {
            difficulty = "extreme";
          } else if (duration > 45 || totalSequences > 6) {
            difficulty = "hard";
          } else if (duration > 30 || totalSequences > 4) {
            difficulty = "medium";
          } else if (count > 0) {
            difficulty = "easy";
          }

          if (active) {
            setSessionDetails((prev) => ({
              ...prev,
              [session.ts_id]: {
                exercises: rawList,
                count,
                duration: duration || session.ts_duration || 45,
                difficulty,
              },
            }));
          }
        } catch (err) {
          console.error("Error fetching session details for card:", err);
        }
      }
    }

    loadAllSessionDetails();
    return () => {
      active = false;
    };
  }, [sessions, sessionDetails]);

  const resolveGroupForSession = useCallback(
    (session) => {
      const gid = session?.gr_id || session?.groupDetails?.gr_id;
      if (gid && coachHomeRows.length) {
        const found = coachHomeRows.find((g) => homeGroupRowGrId(g) === gid);
        if (found) return found;
      }
      return selectedgrp;
    },
    [coachHomeRows, selectedgrp]
  );

  const formatSessionDateTime = (dateValue, timeValue) => {
    const datePart = dateValue
      ? moment(dateValue).locale(localeCode).format("dddd D MMM")
      : "";
    const timeParsed = moment(timeValue, ["HH:mm:ss", "HH:mm", "h:mm A"], true);
    const timePart = timeParsed.isValid()
      ? timeParsed.locale(localeCode).format("LT")
      : timeValue?.slice?.(0, 5) || "";
    return [datePart, timePart].filter(Boolean).join(" • ");
  };

  const rawSessionStateId = (session) =>
    session?.tbl_state_te?.st_id || session?.state_te?.st_id || session?.te_state || null;

  const sessionSlotIsOver = (session) => {
    if (session?.is_completed === true) return true;
    const day = session?.ts_date ? moment(session.ts_date).startOf("day") : null;
    if (day?.isValid() && day.isBefore(moment().startOf("day"))) return true;
    if (!session?.ts_date || session.ts_start_time == null || session.ts_duration == null) {
      return false;
    }
    const start = moment(session.ts_date);
    const time = moment(session.ts_start_time, ["HH:mm:ss", "HH:mm", "h:mm A"], true);
    if (!start.isValid()) return false;
    if (time.isValid()) {
      start.hours(time.hours()).minutes(time.minutes()).seconds(time.seconds() || 0).milliseconds(0);
    }
    const end = start
      .clone()
      .add(Number(session.ts_duration) || 0, "minutes")
      .add(30, "minutes");
    return moment().isSameOrAfter(end);
  };

  const getSessionStateId = (session) => {
    const raw = rawSessionStateId(session);
    if (
      (raw === "st202406071447279901" || raw === "st202406071447279902") &&
      sessionSlotIsOver(session)
    ) {
      return "st202406071447279903";
    }
    return raw;
  };

  const getSessionStateClass = (session) => {
    const stateId = getSessionStateId(session);
    if (stateId === "st202406071447279901") return "running";
    if (stateId === "st202406071447279902") return "paused";
    if (stateId === "st202406071447279903") return "finalized";
    return "scheduled";
  };

  const getSessionStateLabel = (session) => {
    const raw = rawSessionStateId(session);
    const stateId = getSessionStateId(session);
    if (stateId === "st202406071447279903" && raw !== stateId) {
      return t("finalized") || "Finalized";
    }
    const localizedFromApi =
      session?.tbl_state_te?.[`st_name_${currentlng}`] ||
      session?.state_te?.[`st_name_${currentlng}`] ||
      session?.tbl_state_te?.st_name_en ||
      session?.state_te?.st_name_en ||
      session?.tbl_state_te?.st_name ||
      session?.state_te?.st_name;
    if (localizedFromApi) return localizedFromApi;
    if (stateId === "st202406071447279903") return t("finalized") || "Finalized";
    if (stateId === "st202406071447279901") return t("sessionStateLive") || t("running") || "In progress";
    if (stateId === "st202406071447279902") return t("paused") || "Paused";
    return t("scheduled") || "Scheduled";
  };

  const getSessionCardHint = (session) => {
    const stateId = getSessionStateId(session);
    if (isPlayerUi) {
      if (stateId === "st202406071447279901") {
        return t("sessionCardPlayerRunningHint") || "In progress — join live session";
      }
      if (stateId === "st202406071447279902") {
        return t("sessionCardPlayerPausedHint") || "Paused — join when training resumes";
      }
      if (stateId === "st202406071447279903") {
        return (
          t("sessionCardPlayerFinalizedHint") ||
          "Completed — view session plan and your results"
        );
      }
      return t("sessionCardPlayerScheduledHint") || "Scheduled — register or view session plan";
    }
    if (stateId === "st202406071447279901") {
      return t("sessionCardRunningHint") || "In progress — tap to manage exercises";
    }
    if (stateId === "st202406071447279902") {
      return t("sessionCardPausedHint") || "Paused — tap to resume when ready";
    }
    if (stateId === "st202406071447279903") {
      return t("sessionCardFinalizedHint") || "Completed — view results and summary";
    }
    return t("sessionCardScheduledHint") || "Scheduled — tap to open planning";
  };

  const isSessionStarted = (session) => {
    const stateId = getSessionStateId(session);
    return (
      stateId === "st202406071447279901" ||
      stateId === "st202406071447279902" ||
      stateId === "st202406071447279903"
    );
  };

  const handleSessionClick = (session) => {
    setSelectedSession(session);
    if (session) {
      const sessionToStore = {
        ...session,
        gr_id: session.gr_id || session._groupId || null,
      };
      localStorage.setItem("rsessn", JSON.stringify(sessionToStore));
      const grp = resolveGroupForSession(session);
      if (grp?.groupDetails) {
        try {
          localStorage.setItem("grpinf", JSON.stringify(grp));
        } catch {
          /* ignore */
        }
      }
      window.dispatchEvent(
        new CustomEvent("sessionSelected", {
          detail: { session: sessionToStore },
        })
      );

      const stateId = getSessionStateId(session);
      const isFinalized = stateId === "st202406071447279903";

      if (isFinalized) {
        setPlayerSelectedSessionForResults(session);
        setPlayerSessionResultOpen(true);
      } else if (isPlayerUi) {
        setPlayerSelectedSessionForResults(session);
        setPlayerSessionResultOpen(true);
      }
    } else {
      localStorage.removeItem("rsessn");
      window.dispatchEvent(new CustomEvent("sessionSelected"));
    }
  };

  const handlePlayerSessionNavigate = (session, event) => {
    if (event) event.stopPropagation();
    handleSessionClick(session);
    const mobileLive = resolveMobileSessionNavPath(session);
    if (mobileLive) {
      if (!isPlayerUi) {
        ensureCoachRoleForLiveTrainingEntry(user, permissions);
      }
      persistLiveTrainingSession(
        {
          ...session,
          gr_id: session.gr_id || session._groupId || null,
        },
        resolveGroupForSession(session)
      );
      navigate(mobileLive);
      return;
    }
    const stateId = getSessionStateId(session);
    if (
      stateId === "st202406071447279901" ||
      stateId === "st202406071447279902"
    ) {
      navigate("/training");
    } else if (isPlayerUi) {
      setPlayerSelectedSessionForResults(session);
      setPlayerSessionResultOpen(true);
    } else {
      navigate("/planning", { state: { session } });
    }
  };

  const fetchAllCoachHomeSessions = useCallback(async () => {
    const gen = ++listFetchGenRef.current;
    const dateKey = selectedDate
      ? moment(selectedDate).format("YYYY-MM-DD")
      : "";
    if (tourDemo && playerHomeMode) {
      const demo = demoSessionsForDate(dateKey);
      if (gen !== listFetchGenRef.current) return demo;
      setAllSessions(demo);
      return demo;
    }
    if (!dateKey) {
      if (gen !== listFetchGenRef.current) return [];
      setAllSessions([]);
      return [];
    }
    try {
      const entries = coachHomeRows
        .map((g) => ({
          gr_id: homeGroupRowGrId(g),
          name: homeGroupRowName(g),
        }))
        .filter((e) => e.gr_id);
      const nameByGr = new Map(entries.map((e) => [String(e.gr_id), e.name]));
      const listParams = {
        selected_date: dateKey,
      };
      if (entries.length) {
        listParams.gr_ids = entries.map((e) => e.gr_id);
      } else if (!playerHomeMode) {
        if (gen !== listFetchGenRef.current) return [];
        setAllSessions([]);
        return [];
      }
      const response = await sessionlist(listParams);
      let merged =
        response?.data?.group ||
        response?.data?.data ||
        (Array.isArray(response?.data) ? response.data : []) ||
        [];
      merged = merged.map((s) => {
        const grId = s.gr_id;
        return {
          ...s,
          gr_id: grId,
          _coachHomeGroupName: nameByGr.get(String(grId || "")) || "",
        };
      });
      const byTs = new Map();
      merged.forEach((s) => {
        if (s.ts_id && !byTs.has(s.ts_id)) byTs.set(s.ts_id, s);
      });
      merged = Array.from(byTs.values());
      merged.sort((a, b) => {
        const dateA = moment(a.ts_date);
        const dateB = moment(b.ts_date);
        if (dateA.isBefore(dateB)) return -1;
        if (dateA.isAfter(dateB)) return 1;
        const numA = a.ts_session_number || 0;
        const numB = b.ts_session_number || 0;
        return numA - numB;
      });
      if (gen !== listFetchGenRef.current) return merged;
      setAllSessions(merged);

      if (playerHomeMode && merged.length > 0) {
        const playerDataMap = {};
        for (const session of merged) {
          const grId = session.gr_id;
          if (!grId || !session.ts_id) continue;
          try {
            const playerResponse = await grpplayerlist({
              ts_id: session.ts_id,
              gr_id: grId,
            });
            const allPlayers = playerResponse.data.data || [];
            const meData = allPlayers.find((p) => p.user_id === user?.user_id);
            if (meData) {
              playerDataMap[session.ts_id] = meData;
            }
          } catch (error) {
            console.error(
              `Error fetching player data for session ${session.ts_id}:`,
              error
            );
          }
        }
        if (gen !== listFetchGenRef.current) return merged;
        setSessionPlayerData(playerDataMap);
      }
      return merged;
    } catch (error) {
      console.log(error);
      if (gen !== listFetchGenRef.current) return [];
      setAllSessions([]);
      return [];
    }
  }, [coachHomeRows, playerHomeMode, user?.user_id, selectedDate, tourDemo]);

  useEffect(() => {
    const fetchKey = sessionsFetchKey;

    if (!fetchKey) {
      listFetchGenRef.current += 1;
      setAllSessions([]);
      setSessionListLoading(false);
      setSettledFetchKey(null);
      return;
    }

    if (waitingForCoachGroupList) {
      listFetchGenRef.current += 1;
      setSessionListLoading(true);
      setAllSessions([]);
      return;
    }
    if (aggregateCoachSessionsByDate) {
      setSessionListLoading(true);
      setAllSessions([]);
      fetchAllCoachHomeSessions().finally(() => {
        setSessionListLoading(false);
        setSettledFetchKey(fetchKey);
      });
      return;
    }
    if (selectedgrp && selectedgrp.groupDetails && selectedgrp.groupDetails.gr_id) {
      setSessionListLoading(true);
      getsession(selectedgrp.groupDetails.gr_id).finally(() => {
        setSessionListLoading(false);
        setSettledFetchKey(fetchKey);
      });
    } else {
      setAllSessions([]);
      setSessionListLoading(false);
      setSettledFetchKey(null);
    }
  }, [
    selectedgrp,
    aggregateCoachSessionsByDate,
    waitingForCoachGroupList,
    fetchAllCoachHomeSessions,
    sessionsFetchKey,
  ]);

  const getsession = async (id) => {
    const gen = ++listFetchGenRef.current;
    const dateKey = selectedDate
      ? moment(selectedDate).format("YYYY-MM-DD")
      : "";
    if (!dateKey) {
      if (gen !== listFetchGenRef.current) return [];
      setAllSessions([]);
      return [];
    }
    try {
      const response = await sessionlist({
        gr_id: id,
        selected_date: dateKey,
      });
      if (gen !== listFetchGenRef.current) return [];
      let allSessionsData = response.data.group || [];
      
      // Sort sessions by date (ascending) and then by session number (ascending)
      allSessionsData.sort((a, b) => {
        // First sort by date
        const dateA = moment(a.ts_date);
        const dateB = moment(b.ts_date);
        if (dateA.isBefore(dateB)) return -1;
        if (dateA.isAfter(dateB)) return 1;
        
        // If dates are equal, sort by session number
        const numA = a.ts_session_number || 0;
        const numB = b.ts_session_number || 0;
        return numA - numB;
      });
      
      if (gen !== listFetchGenRef.current) return allSessionsData;
      setAllSessions(allSessionsData); // Store all sessions (sorted)
      
      // Fetch player data for current user for each session (if player UI)
      if (isPlayerUi) {
        const playerDataMap = {};
        for (const session of allSessionsData) {
          try {
            const playerParams = {
              ts_id: session.ts_id,
              gr_id: id,
            };
            const playerResponse = await grpplayerlist(playerParams);
            const allPlayers = playerResponse.data.data || [];
            const meData = allPlayers.find((p) => p.user_id === user?.user_id);
            if (meData) {
              playerDataMap[session.ts_id] = meData;
            }
          } catch (error) {
            console.error(`Error fetching player data for session ${session.ts_id}:`, error);
          }
        }
        if (gen !== listFetchGenRef.current) return allSessionsData;
        setSessionPlayerData(playerDataMap);
      }
      return allSessionsData;
    } catch (error) {
      console.log(error);
      if (gen !== listFetchGenRef.current) return [];
      setAllSessions([]);
      return [];
    }
  };

  const refreshSessionsAfterChange = (grIdOverride) => {
    if (aggregateCoachSessionsByDate) {
      return fetchAllCoachHomeSessions();
    }
    const gid =
      grIdOverride ||
      addDialogGroup?.groupDetails?.gr_id ||
      selectedgrp?.groupDetails?.gr_id;
    if (gid) {
      return getsession(gid);
    }
    return Promise.resolve();
  };

  useEffect(() => {
    const onSessionCreated = () => {
      if (!sessionsFetchKey) return;
      refreshSessionsAfterChange();
    };
    window.addEventListener("sessionCreated", onSessionCreated);
    window.addEventListener("smarttt-session-changed", onSessionCreated);
    return () => {
      window.removeEventListener("sessionCreated", onSessionCreated);
      window.removeEventListener("smarttt-session-changed", onSessionCreated);
    };
    // refreshSessionsAfterChange closes over latest fetch helpers for this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionsFetchKey, aggregateCoachSessionsByDate, selectedgrp, playerHomeMode]);

  const handleadd = async (duration, time, date, endDate = null, selectedDays = null, typ = null, gr_id = null) => {
    try {
      console.log("🔍 SessionsList - handleadd called with:", {
        duration,
        time,
        date,
        endDate,
        selectedDays,
        typ,
        gr_id,
        selectedDaysType: typeof selectedDays,
        selectedDaysIsArray: Array.isArray(selectedDays),
        selectedDaysValue: selectedDays
      });

      const grp =
        (addDialogGroup?.groupDetails?.gr_id && addDialogGroup) ||
        (selectedgrp?.groupDetails?.gr_traning_session_type !== "auto"
          ? selectedgrp
          : null);

      if (!grp?.groupDetails) {
        console.error("⚠️ SessionsList - Cannot add session: no group selected");
        return;
      }

      // Check if it's Seasonal mode (has endDate and selectedDays)
      // Get the type from the group or from the typ parameter
      const trainingMode =
        type || (grp?.groupDetails?.tbl_gen_mode_t || grp?.groupDetails?.tbl_gen_mode_ts);
      
      // Validate trainingMode exists before proceeding
      if (!trainingMode) {
        console.error("⚠️ SessionsList - Cannot add session: training mode is not defined");
        alert(t("trainingModeNotDefined") || "Training mode is not defined for this group. Please contact an administrator.");
        return;
      }
      
      // Validate selectedDays is an array
      if (endDate && selectedDays) {
        if (!Array.isArray(selectedDays)) {
          console.error("❌ SessionsList - selectedDays is not an array:", selectedDays);
          console.error("   - Type:", typeof selectedDays);
          console.error("   - Value:", selectedDays);
          alert(t("errorCreatingSessions") || "Error: Selected days must be an array");
          return;
        }
      }
      
      if (endDate && selectedDays && Array.isArray(selectedDays) && selectedDays.length > 0 && trainingMode?.ts_gen_mode === "Seasonal") {
        // Seasonal mode: create multiple sessions
        console.log("🔄 SessionsList - handleadd - Parameters received:", {
          duration,
          time,
          date,
          endDate,
          selectedDays,
          typ,
          gr_id,
          selectedDaysType: typeof selectedDays,
          selectedDaysIsArray: Array.isArray(selectedDays),
          selectedDaysLength: selectedDays?.length
        });
        
        // Use gr_id parameter if provided, otherwise use from resolved group
        const groupId = gr_id || grp.groupDetails.gr_id;
        // Use typ parameter if provided, otherwise use from trainingMode
        const typeId = typ || trainingMode?.te_gen_mode_id;
        
        if (!typeId) {
          console.error("⚠️ SessionsList - Cannot create seasonal sessions: type ID is missing");
          alert(t("trainingModeNotDefined") || "Training mode is not defined for this group. Please contact an administrator.");
          return;
        }
        
        console.log("🔄 SessionsList - handleadd calling createSeasonalSessions with:", {
          duration,
          time,
          startDate: date,
          endDate,
          selectedDays,
          typ: typeId,
          gr_id: groupId,
          trainingMode: trainingMode?.ts_gen_mode
        });
        
        const response = await createSeasonalSessions(
          duration,
          time,
          date,
          endDate,
          selectedDays,
          typeId,
          groupId
        );
        setaddsession(false);
        setAddDialogGroup(null);
        setsuccess(response.data.message || t("seasonalSessionsCreated") || `${response.data.data?.sessionsCreated || 0} seasonal sessions created successfully`);
        setOpen(true);
        refreshSessionsAfterChange(groupId);
        
        // Dispatch event to notify TodaySessions to refresh
        if (response && response.data && response.data.status === "success") {
          const today = moment().startOf("day");
          const createdSessions = response.data.data?.sessions || [];
          
          // Check if any created session is for today
          const hasTodaySession = createdSessions.some(session => {
            if (!session.ts_date) return false;
            const sessionDate = moment(session.ts_date).startOf("day");
            return sessionDate.isSame(today, "day");
          });
          
          if (hasTodaySession) {
            console.log("📢 SessionsList - Dispatching sessionCreated event for today's seasonal session");
            window.dispatchEvent(new CustomEvent("sessionCreated", { 
              detail: { sessions: createdSessions } 
            }));
          }
        }
      } else if (
        grp.groupDetails.gr_traning_session_type ===
        "tsgm2024091410200911"
      ) {
        // Wizard mode - ensure trainingMode is passed
        if (!trainingMode || !trainingMode.te_gen_mode_id) {
          console.error("⚠️ SessionsList - Cannot navigate to Wizard: training mode is missing");
          alert(t("trainingModeNotDefined") || "Training mode is not defined for this group. Please contact an administrator.");
          return;
        }
        setAddDialogGroup(null);
        navigate("/Wizardsession", {
          state: {
            min: duration,
            date: date,
            time: time,
            type: trainingMode, // Use trainingMode instead of type
            grpid: grp.groupDetails.gr_id,
          },
        });
      } else {
        // Manual mode: create single session
        const response = await createsession(
          duration,
          time,
          date,
          typ,
          grp.groupDetails.gr_id
        );
        
        // Close the dialog immediately
        setaddsession(false);
        setAddDialogGroup(null);
        setsuccess(response.data.message);
        setOpen(true);
        
        // After session is created, automatically select it and show exercises tab
        if (response && response.data && response.data.status === "success") {
          // The response contains the created session in response.data.group
          const createdSession = response.data.group;
          
          if (createdSession && createdSession.ts_id) {
            console.log("✅ SessionsList - New session created, auto-selecting:", createdSession.ts_id);
            
            // Add gr_id to the session object if not present
            const newSession = {
              ...createdSession,
              gr_id: createdSession.gr_id || grp.groupDetails.gr_id
            };
            
            // Select the new session immediately
            handleSessionClick(newSession);
            console.log("✅ SessionsList - New session selected, navigating to exercises page");
            
            // Pass session in navigation state (Planning prefers location.state.session)
            navigate("/planning", { state: { session: newSession } });
            
            // Refresh session list in background (after selection)
            refreshSessionsAfterChange(grp.groupDetails.gr_id);
          } else {
            // If session data not in response, refresh list and find it
            refreshSessionsAfterChange(grp.groupDetails.gr_id).then((sessions) => {
              const list = Array.isArray(sessions) ? sessions : [];
              const sessionDate = moment(date).format("YYYY-MM-DD");
              const sessionTime = time.slice(0, 5); // HH:mm format
              
              const newSession = list.find(s => {
                  if (!s.ts_date) return false;
                  const sDate = moment(s.ts_date).format("YYYY-MM-DD");
                  const sTime = s.ts_start_time ? s.ts_start_time.slice(0, 5) : null;
                  return sDate === sessionDate && sTime === sessionTime;
                }) || null;
                
                if (newSession) {
                  const sessionWithGrId = {
                    ...newSession,
                    gr_id: newSession.gr_id || grp.groupDetails.gr_id
                  };
                  handleSessionClick(sessionWithGrId);
                  console.log("✅ SessionsList - New session found and selected, navigating to exercises page");
                  navigate("/planning", { state: { session: sessionWithGrId } });
                }
            });
          }
          
          // Dispatch event to notify TodaySessions to refresh
          const sessionDate = moment(date, "DD/MM/YYYY");
          const today = moment().startOf("day");
          
          // Only trigger refresh if the session is for today
          if (sessionDate.isSame(today, "day")) {
            console.log("📢 SessionsList - Dispatching sessionCreated event for today's session");
            window.dispatchEvent(new CustomEvent("sessionCreated", { 
              detail: { session: response.data.group || createdSession } 
            }));
          }
        } else {
          // If no session in response, just refresh the list
          refreshSessionsAfterChange(grp.groupDetails.gr_id);
        }
      }
    } catch (error) {
      console.log(error);
      setOpen2(true);
      seterror(error.response?.data?.message || error.message || t("errorCreatingSessions") || "Error creating sessions");
    }
  };
  const handleOpenSessionPlanning = (session, event) => {
    event.stopPropagation();
    handleSessionClick(session);
    navigate("/planning", { state: { session } });
  };

  const handleOpenSessionResults = (session, event) => {
    event.stopPropagation();
    handleSessionClick(session);
    setPlayerSelectedSessionForResults(session);
    setPlayerSessionResultOpen(true);
  };

  const handleOpenSessionPresence = (session, event) => {
    event?.stopPropagation?.();
    handleSessionClick(session);
    setPresenceDialogSession(session);
    setPresenceDialogOpen(true);
  };
  const handleplay = (session) => {
    const grp = resolveGroupForSession(session);
    navigate("/CoachExerciseControl", {
      state: {
        session,
        selectedgrp: grp,
      },
    });
  };

  const handleControlExercise = (session) => {
    const grp = resolveGroupForSession(session);
    navigate("/CoachExerciseControl", {
      state: {
        session: session,
        selectedgrp: grp,
      },
    });
  };

  const handleDeleteSession = async (session, e) => {
    e.stopPropagation();
    const sessionGrId = session?.gr_id || selectedgrp?.groupDetails?.gr_id;
    if (!session?.ts_id || !sessionGrId) return;
    if (session.tbl_state_te?.st_id === "st202406071447279901") {
      seterror(t("cannotDeleteRunningSession") || "Cannot delete a session in progress");
      setOpen2(true);
      return;
    }
    if (
      !window.confirm(
        t("confirmDeleteSession", { n: session.ts_session_number }) ||
          `Delete session #${session.ts_session_number}? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await deleteTrainingSession({
        ts_id: session.ts_id,
        gr_id: sessionGrId,
      });
      if (selectedSession?.ts_id === session.ts_id) {
        setSelectedSession(null);
        localStorage.removeItem("rsessn");
        window.dispatchEvent(new CustomEvent("sessionSelected"));
      }
      setsuccess(t("sessionDeleted") || "Session deleted");
      setOpen(true);
      refreshSessionsAfterChange();
    } catch (err) {
      setOpen2(true);
      seterror(err?.message || t("errorDeletingSession") || "Could not delete session");
    }
  };

  const canDuplicateSessionRow = (session) => {
    if (!canManageSessions) return false;
    if (getSessionStateId(session) === "st202406071447279901") return false;
    const grp = resolveGroupForSession(session);
    const trainingMode =
      grp?.groupDetails?.gr_traning_session_type ||
      selectedgrp?.groupDetails?.gr_traning_session_type;
    return trainingMode !== "auto";
  };

  const openDuplicateDialog = (session, e) => {
    e.stopPropagation();
    setDuplicateDialogSession(session);
    setDupCalendarDate(
      selectedDate ? moment(selectedDate).startOf("day").toDate() : new Date()
    );
    const tval = session.ts_start_time;
    setDupTime(
      typeof tval === "string" && tval.length >= 5 ? tval.slice(0, 5) : ""
    );
    const sessionGrId = session.gr_id || session.groupDetails?.gr_id;
    if (needsGroupPicker && sessionGrId && selectableGroups.length) {
      const row = selectableGroups.find((g) => homeGroupRowGrId(g) === sessionGrId);
      setDupDialogGroup(row || null);
    } else {
      setDupDialogGroup(null);
    }
  };

  const duplicateTargetGroupName = useMemo(() => {
    if (needsGroupPicker) return "";
    if (selectedgrp?.groupDetails?.gr_traning_session_type !== "auto") {
      return homeGroupRowName(selectedgrp) || "";
    }
    if (duplicateDialogSession) {
      const grp = resolveGroupForSession(duplicateDialogSession);
      return homeGroupRowName(grp) || duplicateDialogSession._coachHomeGroupName || "";
    }
    return "";
  }, [needsGroupPicker, selectedgrp, duplicateDialogSession, resolveGroupForSession]);

  const handleDuplicateConfirm = async () => {
    if (!duplicateDialogSession || !dupTime) {
      seterror(t("timeRequired") || "Please select a start time");
      setOpen2(true);
      return;
    }
    if (needsGroupPicker && selectableGroups.length > 0 && !homeGroupRowGrId(dupDialogGroup)) {
      seterror(t("groupRequired") || "Please select a group");
      setOpen2(true);
      return;
    }
    const sourceGrId =
      duplicateDialogSession.gr_id ||
      duplicateDialogSession.groupDetails?.gr_id ||
      homeGroupRowGrId(resolveGroupForSession(duplicateDialogSession));
    // Target group for the new session (picker / current selection), falling back to source.
    const sessionGrId =
      homeGroupRowGrId(dupDialogGroup) ||
      (selectedgrp?.groupDetails?.gr_traning_session_type !== "auto"
        ? homeGroupRowGrId(selectedgrp)
        : null) ||
      sourceGrId;
    if (!sessionGrId) {
      seterror(t("groupRequired") || "Please select a group");
      setOpen2(true);
      return;
    }
    if (!duplicateDialogSession.ts_id) {
      seterror(t("errorDuplicatingSession") || "Could not duplicate session");
      setOpen2(true);
      return;
    }
    setDupSubmitting(true);
    try {
      const response = await duplicateTrainingSession({
        source_ts_id: duplicateDialogSession.ts_id,
        gr_id: sessionGrId,
        ts_date: moment(dupCalendarDate).format("DD/MM/YYYY"),
        ts_start_time: dupTime,
      });
      setDuplicateDialogSession(null);
      setDupDialogGroup(null);
      setsuccess(
        response.data.message ||
          t("sessionDuplicated") ||
          "Session duplicated"
      );
      setOpen(true);
      const created = response.data.group;
      if (created && created.ts_id) {
        const newSession = {
          ...created,
          gr_id: sessionGrId,
        };
        handleSessionClick(newSession);
        if (selectedgrp?.groupDetails?.gr_id === sessionGrId) {
          try {
            localStorage.setItem("grpinf", JSON.stringify(selectedgrp));
            window.dispatchEvent(new CustomEvent("groupSelected"));
          } catch {
            // ignore
          }
        }
        navigate("/planning");
      }
      refreshSessionsAfterChange();
    } catch (err) {
      setOpen2(true);
      seterror(
        err?.message ||
          t("errorDuplicatingSession") ||
          "Could not duplicate session"
      );
    } finally {
      setDupSubmitting(false);
    }
  };

  const duplicateButton = (session) =>
    canDuplicateSessionRow(session) ? (
      <p
        onClick={(e) => openDuplicateDialog(session, e)}
        title={t("duplicateSession") || "Duplicate session"}
      >
        <ContentCopyIcon style={{ fontSize: "22px" }} />
      </p>
    ) : null;

  const handlepresence = async (session, presence) => {
    try {
      if (presence && user && user.emailVerification !== true && user.is_demo_walkin !== true) {
        alert(
          t("emailVerificationRequiredBody") ||
            "Please confirm your email before joining a training session. Check your inbox or resend the link from Home."
        );
        return;
      }
      const playerData = sessionPlayerData[session.ts_id];
      if (!playerData) {
        console.error("Player data not found for session");
        return;
      }
      const grId =
        session.gr_id ||
        selectedgrp?.groupDetails?.gr_id ||
        resolveGroupForSession(session)?.groupDetails?.gr_id;
      if (!grId) {
        console.error("Group not found for session");
        return;
      }
      const params = {
        ts_id: session.ts_id,
        gr_id: grId,
        ts_present: presence,
        player_id: playerData.player_id,
      };
      const response = await playerpresent(params);
      if (response?.data?.status === "error") {
        alert(
          response.data.message ||
            t("emailVerificationRequiredBody") ||
            "Unable to mark presence"
        );
        return;
      }
      // Refresh player data for this session
      try {
        const playerParams = {
          ts_id: session.ts_id,
          gr_id: grId,
        };
        const playerResponse = await grpplayerlist(playerParams);
        const allPlayers = playerResponse.data.data || [];
        const meData = allPlayers.find((p) => p.user_id === user?.user_id);
        if (meData) {
          setSessionPlayerData(prev => ({
            ...prev,
            [session.ts_id]: meData
          }));
        }
      } catch (error) {
        console.error("Error refreshing player data:", error);
      }
    } catch (error) {
      const apiMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      if (apiMsg) alert(apiMsg);
      console.log(error);
    }
  };

  const handleSessionTypeSelected = async (sessionType) => {
    if (!sessionCreationGroup?.groupDetails?.gr_id) {
      alert(t("selectGroupFirst") || "Please select a group");
      return;
    }
    setSelectedSessionType(sessionType);
    setSessionTypeDialog(false);
    
    // Fetch training modes to find the appropriate type
    try {
      const { contenttypelist } = await import("./funtionsBD/Mainservice");
      const response = await contenttypelist();
      if (response.data.status === "success") {
        const allModes = response.data.data || [];
        // Find the mode based on selected session type
        let trainingMode;
        if (sessionType === "seasonal") {
          trainingMode = allModes.find(m => m.ts_gen_mode === "Seasonal");
        } else if (sessionType === "spontaneous") {
          trainingMode = allModes.find(m => m.ts_gen_mode === "Manual");
        }
        
        if (!trainingMode) {
          console.error("⚠️ SessionsList - Cannot find training mode for session type:", sessionType);
          alert(t("trainingModeNotFound") || `Training mode for ${sessionType} session type not found. Please contact an administrator.`);
          return;
        }
        
        settype(trainingMode);
        setaddsession(true);
      }
    } catch (error) {
      console.error("Error fetching training modes:", error);
      alert(t("errorLoadingTrainingModes") || "Error loading training modes. Please try again.");
    }
  };

  // Debug: Log conditions for button visibility
  useEffect(() => {
    const sessionAddPerm = permissions?.find(p => p.enum_value === "SESSION_ADD");
    console.log("🔍 SessionsList - Button visibility check:", {
      hasSelectedGrp: !!selectedgrp,
      groupDetails: selectedgrp?.groupDetails,
      gr_traning_session_type: selectedgrp?.groupDetails?.gr_traning_session_type,
      isAuto: selectedgrp?.groupDetails?.gr_traning_session_type === "auto",
      hasSessionAddPermission: hasPermission("SESSION_ADD"),
      sessionAddPermissionFound: !!sessionAddPerm,
      sessionAddPermission: sessionAddPerm,
      allPermissions: permissions?.map(p => p.enum_value) || [],
      totalPermissions: permissions?.length || 0
    });
  }, [selectedgrp, permissions]);

  // Listen for openAddSessionDialog event from AddSessionCard (home page)
  useEffect(() => {
    const handleOpenDialog = (event) => {
      if (isPlayerUi || !canManageSessions) return;
      const detail = event?.detail || {};
      const fromEvent = Array.isArray(detail.availableGroups)
        ? detail.availableGroups
        : [];
      const fromProps = Array.isArray(addSessionGroupOptionsRef.current)
        ? addSessionGroupOptionsRef.current
        : [];
      const fromRaw = Array.isArray(rawGroupsForPickerRef.current)
        ? rawGroupsForPickerRef.current
        : [];
      const preferredRows =
        fromEvent.length > 0
          ? fromEvent
          : fromProps.length > 0
            ? fromProps
            : fromRaw.length > 0
              ? fromRaw
              : selectableGroupsRef.current;

      const initial = resolveDefaultAddSessionGroup({
        preferredRows,
        selectedgrp: selectedgrpRef.current,
        eventSelectedgrp: detail.selectedgrp || null,
      });
      setAddDialogGroup(initial);
      if (initial && typeof onSessionAddGroupChosenRef.current === "function") {
        onSessionAddGroupChosenRef.current(initial);
      }
      setSessionTypeDialog(true);
    };

    window.addEventListener("openAddSessionDialog", handleOpenDialog);

    return () => {
      window.removeEventListener("openAddSessionDialog", handleOpenDialog);
    };
  }, [isPlayerUi, canManageSessions]);

  useEffect(() => {
    const dismissTourDialogs = (event) => {
      if (event?.detail?.keep?.addSession) return;
      setSessionTypeDialog(false);
    };
    window.addEventListener(CLOSE_TOUR_TRANSIENT_UI_EVENT, dismissTourDialogs);
    return () => window.removeEventListener(CLOSE_TOUR_TRANSIENT_UI_EVENT, dismissTourDialogs);
  }, []);

  return (
    <div className="sessions-list">
      {selectedgrp?.groupDetails?.gr_traning_session_type === "auto" ? (
        <div style={{ padding: "10px", color: "#666", fontStyle: "italic" }}>
          {t("autoSessionMode") || "Automatic session mode - sessions are created automatically"}
        </div>
      ) : selectedgrp && !isPlayerUi && !canManageSessions ? (
        <div style={{ padding: "10px", color: "#ff9800", fontStyle: "italic" }}>
          {t("noPermissionToAddSession") || "You don't have permission to add training sessions"}
        </div>
      ) : null}

      {selectedDate && sessionsDataPending && (
        <div
          role="status"
          aria-live="polite"
          className="home-sessions-loading-state"
        >
          <span className="home-sessions-loading-spinner" aria-hidden="true" />
          {t("loadingSessionsForDate") || t("loading")}
        </div>
      )}

      {selectedDate && !sessionsDataPending && sessions.length === 0 && (
        <div
          role="status"
          className="home-sessions-empty-state"
        >
          {t("noSessionsScheduled")}
        </div>
      )}

      <ul>
        {sessions.map((session, index) => {
          const stateClass = getSessionStateClass(session);
          const started = isSessionStarted(session);
          return (
          <li
            key={session.ts_id || index}
            className={[
              "home-session-card",
              stateClass,
              started ? "home-session-card--started" : "",
              selectedSession?.ts_id != null &&
              String(selectedSession.ts_id) === String(session.ts_id)
                ? "active home-session-card--selected"
                : "",
            ].filter(Boolean).join(" ")}
            aria-selected={
              selectedSession?.ts_id != null &&
              String(selectedSession.ts_id) === String(session.ts_id)
            }
            onClick={(e) => {
              if (isPlayerUi) {
                handlePlayerSessionNavigate(session, e);
                return;
              }
              handleSessionClick(session);
              const stateId = getSessionStateId(session);
              // Finalized sessions open results via handleSessionClick.
              if (stateId === "st202406071447279903") return;
              navigate("/planning", { state: { session } });
            }}
            onDoubleClick={(e) => {
              if (isPlayerUi) {
                handlePlayerSessionNavigate(session, e);
                return;
              }
              handleSessionClick(session);
              const stateId = getSessionStateId(session);
              if (stateId === "st202406071447279903") return;
              navigate("/planning", { state: { session } });
            }}
          >
            <span className="home-session-card__accent" aria-hidden="true" />

            <div className="session-info home-session-card__body">
              <div className="home-session-card__title-row">
                <h3>
                  {formatTrainingSessionLabel(session, t)}
                </h3>
                {session._coachHomeGroupName && (
                  <span className="home-session-card__group">
                    {session._coachHomeGroupName}
                  </span>
                )}
              </div>
              
              <p className="home-session-card__datetime">
                {formatSessionDateTime(session.ts_date, session.ts_start_time)}
              </p>
              
              {sessionDetails[session.ts_id] && (
                <div className="home-session-card__meta">
                  <span className={`home-session-card__difficulty home-session-card__difficulty--${sessionDetails[session.ts_id].difficulty}`}>
                    {t(`difficulty_${sessionDetails[session.ts_id].difficulty}`) || sessionDetails[session.ts_id].difficulty}
                  </span>
                  <span className="home-session-card__meta-separator">•</span>
                  <span className="home-session-card__duration">
                    {t("duration_mins", { minutes: sessionDetails[session.ts_id].duration })}
                  </span>
                  <span className="home-session-card__meta-separator">•</span>
                  <span className="home-session-card__exercises-count">
                    {sessionDetails[session.ts_id].count === 1
                      ? t("exercises_count_one")
                      : t("exercises_count", { count: sessionDetails[session.ts_id].count })}
                  </span>
                </div>
              )}

              {getSessionStateId(session) === "st202406071447279903" &&
                session.hr_highlights && (
                <div className="home-session-card__hr-summary">
                  <ExerciseCardHrSummary
                    metrics="session_highlights"
                    hrHighlights={session.hr_highlights}
                    t={t}
                    className="player-session-results__ex-hr-summary-card home-session-card__hr-summary-card"
                  />
                </div>
              )}

              <p className="home-session-card__hint">{getSessionCardHint(session)}</p>
            </div>

            <div className="session-start home-session-card__aside">
              <p className={`session-state home-session-card__badge home-session-card__badge--${stateClass}`}>
                {stateClass === "running" && (
                  <span className="home-session-card__live-dot" aria-hidden="true" />
                )}
                {getSessionStateLabel(session)}
              </p>
              {/* role */}
              {isPlayerUi ? (
                (() => {
                  const stateId = getSessionStateId(session);
                  const playerData = sessionPlayerData[session.ts_id];
                  const canRegister =
                    playerData &&
                    (playerData.presensee === false || playerData.presensee === null);
                  if (stateId === "st202406071447279900" && canRegister) {
                    return (
                      <div className="session_edit">
                        <button
                          className="deactive_btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlepresence(session, true);
                          }}
                        >
                          {t("register")}
                        </button>
                        <p
                          onClick={(e) => handlePlayerSessionNavigate(session, e)}
                          title={t("viewPlan") || "View session plan"}
                        >
                          <DriveFileRenameOutlineIcon />
                        </p>
                      </div>
                    );
                  }
                  if (stateId === "st202406071447279900") {
                    return (
                      <div className="session_edit">
                        <p
                          onClick={(e) => handlePlayerSessionNavigate(session, e)}
                          title={t("viewPlan") || "View session plan"}
                        >
                          <DriveFileRenameOutlineIcon />
                        </p>
                      </div>
                    );
                  }
                  if (
                    stateId === "st202406071447279901" ||
                    stateId === "st202406071447279902"
                  ) {
                    return (
                      <div className="session_edit">
                        <button
                          className="deactive_btn"
                          onClick={(e) => handlePlayerSessionNavigate(session, e)}
                        >
                          {t("joinSession") || "Join"}
                        </button>
                      </div>
                    );
                  }
                  if (stateId === "st202406071447279903") {
                    return (
                      <div className="session_edit">
                        <p
                          onClick={(e) => handlePlayerSessionNavigate(session, e)}
                          title={t("viewResults") || "View results"}
                        >
                          <VisibilityOutlinedIcon />
                        </p>
                      </div>
                    );
                  }
                  return null;
                })()
              ) : (
                <>
              {getSessionStateId(session) !== "st202406071447279903" &&
              !isCoachStaffUi &&
              role !== 3 ? (
                <div className="session_edit">
                  {" "}
                  <PlayArrowIcon
                    style={{ fontSize: "35px", marginRight: "10px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleplay(session);
                    }}
                  />
                  <p
                    onClick={(e) => handleOpenSessionPlanning(session, e)}
                    title={t("editSchedule") || "Edit schedule"}
                  >
                    <DriveFileRenameOutlineIcon />
                  </p>
                  {duplicateButton(session)}
                  {canManageSessions &&
                    getSessionStateId(session) !== "st202406071447279901" && (
                      <p
                        onClick={(e) => handleDeleteSession(session, e)}
                        title={t("deleteSession") || "Delete session"}
                      >
                        <DeleteOutlineIcon style={{ color: "#c62828" }} />
                      </p>
                    )}
                </div>
              ) : (
                getSessionStateId(session) === "st202406071447279900" &&
                !isCoachStaffUi &&
                role === 3 && (
                  (() => {
                    const playerData = sessionPlayerData[session.ts_id];
                    const canRegister = playerData && (playerData.presensee === false || playerData.presensee === null);
                    return canRegister ? (
                      <button
                        className="deactive_btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlepresence(session, true);
                        }}
                      >
                        {t("register")}
                      </button>
                    ) : null;
                  })()
                )
              )}
              {isCoachStaffUi &&
                getSessionStateId(session) !== "st202406071447279903" &&
                getSessionStateId(session) !== "st202406071447279901" && (
                  <div className="session_edit">
                    <p
                      onClick={(e) => handleOpenSessionPlanning(session, e)}
                      title={t("editSchedule") || "Edit schedule"}
                    >
                      <DriveFileRenameOutlineIcon />
                    </p>
                    {duplicateButton(session)}
                    {canManageSessions && (
                      <p
                        onClick={(e) => handleDeleteSession(session, e)}
                        title={t("deleteSession") || "Delete session"}
                      >
                        <DeleteOutlineIcon style={{ color: "#c62828" }} />
                      </p>
                    )}
                  </div>
                )}
              {getSessionStateId(session) === "st202406071447279903" && (
                <div className="session_edit">
                  <p
                    onClick={(e) => handleOpenSessionResults(session, e)}
                    title={t("viewResults") || "View results"}
                  >
                    <VisibilityOutlinedIcon />
                  </p>
                  {canManagePastSessions && (
                    <p
                      onClick={(e) => handleOpenSessionPlanning(session, e)}
                      title={t("editSchedule") || "Edit schedule"}
                    >
                      <DriveFileRenameOutlineIcon />
                    </p>
                  )}
                  {canCorrectPresence && (
                    <p
                      onClick={(e) => handleOpenSessionPresence(session, e)}
                      title={
                        t("correctSessionPresence") ||
                        t("managePlayerPresence") ||
                        "Correct presence"
                      }
                    >
                      <GroupsOutlinedIcon />
                    </p>
                  )}
                  {duplicateButton(session)}
                  {canManagePastSessions && canManageSessions && (
                    <p
                      onClick={(e) => handleDeleteSession(session, e)}
                      title={t("deleteSession") || "Delete session"}
                    >
                      <DeleteOutlineIcon style={{ color: "#c62828" }} />
                    </p>
                  )}
                </div>
              )}
                </>
              )}
            </div>
          </li>
          );
        })}
      </ul>
      
      {/* Session Type Selection Dialog */}
      <Dialog
        open={sessionTypeDialog}
        onClose={() => {
          setSessionTypeDialog(false);
          setAddDialogGroup(null);
        }}
        maxWidth="sm"
        fullWidth
        className="session-add-type-dialog"
        PaperProps={{
          className: "asd-dialog session-add-dialog home-theme-root",
          "aria-labelledby": "asd-type-title",
        }}
        sx={{
          zIndex: 9999,
          "& .MuiDialog-container": {
            zIndex: 9999,
          },
        }}
      >
        <div className="asd">
          <header className="asd__header">
            <div className="asd__hero" aria-hidden>
              <AddCircleOutlineIcon fontSize="small" />
            </div>
            <div className="asd__header-text">
              <h2 id="asd-type-title" className="asd__title">
                {t("selectSessionType") || "Select Session Type"}
              </h2>
              <p className="asd__hint">
                {t("selectSessionTypeHint") ||
                  "Choose how you want to add sessions to the calendar."}
              </p>
            </div>
            <button
              type="button"
              className="asd__close"
              onClick={() => {
                setSessionTypeDialog(false);
                setAddDialogGroup(null);
              }}
              aria-label={t("cancel") || "Cancel"}
            >
              <CloseIcon fontSize="small" />
            </button>
          </header>

          <div className="asd__body">
            {needsGroupPicker && selectableGroups.length === 0 && (
              <p className="asd__alert" role="alert">
                {t("noManualGroupsForAddSession") ||
                  "No groups allow manual session creation (all may be in automatic mode)."}
              </p>
            )}
            {needsGroupPicker && selectableGroups.length > 0 && (
              <section className="asd__section">
                <label className="asd__label" htmlFor="asd-add-session-group">
                  {t("group") || "Group"}
                </label>
                <select
                  id="asd-add-session-group"
                  className="asd__select"
                  value={
                    homeGroupRowGrId(addDialogGroup)
                      ? String(homeGroupRowGrId(addDialogGroup))
                      : ""
                  }
                  onChange={(e) => {
                    const id = e.target.value;
                    const row = findHomeGroupRowById(selectableGroups, id);
                    setAddDialogGroup(row || null);
                    if (row && typeof onSessionAddGroupChosen === "function") {
                      onSessionAddGroupChosen(row);
                    }
                  }}
                >
                  <option value="">
                    {t("selectGroup") || "Select a group"}
                  </option>
                  {selectableGroups.map((g) => {
                    const gid = homeGroupRowGrId(g);
                    if (!gid) return null;
                    return (
                      <option key={String(gid)} value={String(gid)}>
                        {homeGroupRowName(g) || gid}
                      </option>
                    );
                  })}
                </select>
              </section>
            )}

            <div className="asd__type-list">
              <button
                type="button"
                className={`asd__type-card${
                  selectedSessionType === "spontaneous" ? " is-active" : ""
                }`}
                disabled={sessionTypeActionsDisabled}
                onClick={() => handleSessionTypeSelected("spontaneous")}
              >
                <span className="asd__type-icon" aria-hidden>
                  <EventAvailableOutlinedIcon fontSize="small" />
                </span>
                <span className="asd__type-copy">
                  <span className="asd__type-title">
                    {t("spontaneous") || "Spontaneous"}
                  </span>
                  <span className="asd__type-desc">
                    {t("spontaneousDescription") ||
                      "Create a single session for a specific date"}
                  </span>
                </span>
                <ChevronRightIcon className="asd__type-chevron" fontSize="small" />
              </button>

              <button
                type="button"
                className={`asd__type-card${
                  selectedSessionType === "seasonal" ? " is-active" : ""
                }`}
                disabled={sessionTypeActionsDisabled}
                onClick={() => handleSessionTypeSelected("seasonal")}
              >
                <span className="asd__type-icon" aria-hidden>
                  <DateRangeOutlinedIcon fontSize="small" />
                </span>
                <span className="asd__type-copy">
                  <span className="asd__type-title">
                    {t("seasonal") || "Seasonal"}
                  </span>
                  <span className="asd__type-desc">
                    {t("seasonalDescription") ||
                      "Create multiple sessions with recurring dates"}
                  </span>
                </span>
                <ChevronRightIcon className="asd__type-chevron" fontSize="small" />
              </button>
            </div>
          </div>

          <div className="asd__actions">
            <button
              type="button"
              className="asd__btn asd__btn--ghost"
              onClick={() => {
                setSessionTypeDialog(false);
                setAddDialogGroup(null);
              }}
            >
              {t("cancel") || "Cancel"}
            </button>
          </div>
        </div>
      </Dialog>

      <DuplicateSessionDialog
        open={!!duplicateDialogSession}
        session={duplicateDialogSession}
        calendarDate={dupCalendarDate}
        onCalendarDateChange={setDupCalendarDate}
        time={dupTime}
        onTimeChange={setDupTime}
        submitting={dupSubmitting}
        onClose={() => {
          setDuplicateDialogSession(null);
          setDupDialogGroup(null);
        }}
        onConfirm={handleDuplicateConfirm}
        showGroupPicker={needsGroupPicker && selectableGroups.length > 0}
        selectableGroups={selectableGroups}
        selectedGroupId={homeGroupRowGrId(dupDialogGroup) || ""}
        onGroupChange={(row) => {
          setDupDialogGroup(row);
          if (row && typeof onSessionAddGroupChosen === "function") {
            onSessionAddGroupChosen(row);
          }
        }}
        noGroupsAvailable={needsGroupPicker && selectableGroups.length === 0}
        targetGroupName={duplicateTargetGroupName}
        allowPastDates={canManagePastSessions}
      />

      {type?.ts_gen_mode === "Seasonal" ? (
        <SeasonalSession
          setopen={setaddsession}
          open={addsession}
          handleadd={handleadd}
          type={type}
          selectedgrp={sessionCreationGroup}
          allowPastDates={canManagePastSessions}
        />
      ) : (
        <Manualsession
          setopen={setaddsession}
          open={addsession}
          handleadd={handleadd}
          type={type}
          allowPastDates={canManagePastSessions}
        />
      )}
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={closealert}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert variant="filled" severity="success">
          {success}
        </Alert>
      </Snackbar>
      <Snackbar
        open={open2}
        autoHideDuration={3000}
        onClose={() => setOpen2(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert variant="filled" severity="error">
          {error}
        </Alert>
      </Snackbar>

      <TrainingSessionResultsDialog
        open={playerSessionResultOpen}
        onClose={() => setPlayerSessionResultOpen(false)}
        session={playerSelectedSessionForResults}
        localeCode={localeCode}
        onSelectExercise={(ex, opts) => {
          setPlayerSelectedExerciseForResults(ex);
          setPlayerExerciseResultInitialTab(opts?.initialTab || "perf");
          setPlayerExerciseResultOpen(true);
        }}
        coachMode={!isPlayerUi}
        filterPlayers={coachFilterPlayers}
        playersLoading={coachPlayersLoading}
        selectedPlayerId={coachSelectedPlayerId}
        onPlayerSelect={setCoachSelectedPlayerId}
        viewerParticipatedInSession={viewerParticipatedInSelectedSession}
        onManagePresence={
          canCorrectPresence && playerSelectedSessionForResults
            ? () =>
                handleOpenSessionPresence(playerSelectedSessionForResults)
            : null
        }
        onManageTables={
          canEditFinalizedSeating
            ? (ex) => setSeatingExercise(ex)
            : null
        }
      />

      <FinalizedExerciseSeatingDialog
        open={Boolean(seatingExercise?.te_id && playerSelectedSessionForResults?.ts_id)}
        onClose={() => setSeatingExercise(null)}
        session={playerSelectedSessionForResults}
        selectedgrp={
          resolveGroupForSession(playerSelectedSessionForResults) || selectedgrp
        }
        exercise={seatingExercise}
        roster={coachFilterPlayers}
        user={user}
      />

      {presenceDialogSession ? (
        <PlayerPresenceDialog
          open={presenceDialogOpen}
          onClose={() => {
            setPresenceDialogOpen(false);
            setPresenceDialogSession(null);
          }}
          selectedSession={presenceDialogSession}
          selectedgrp={resolveGroupForSession(presenceDialogSession) || selectedgrp}
          presentation="sheet"
          allowInviteWhenClosed
          presenceCorrectionOnly={
            canManagePastSessions ||
            isSessionClosedForPresence(presenceDialogSession)
          }
          onUpdate={async () => {
            refreshSessionsAfterChange(
              presenceDialogSession?.gr_id ||
                selectedgrp?.groupDetails?.gr_id
            );
          }}
        />
      ) : null}

      {playerSelectedExerciseForResults && (
        <ExerciseResultDialog
          open={playerExerciseResultOpen}
          onClose={() => {
            setPlayerExerciseResultOpen(false);
          }}
          onBack={() => {
            setPlayerExerciseResultOpen(false);
          }}
          initialTab={playerExerciseResultInitialTab}
          exercise={playerSelectedExerciseForResults}
          session={playerSelectedSessionForResults}
          onNavigateExercise={setPlayerSelectedExerciseForResults}
          viewPlayerId={!isPlayerUi ? (coachSelectedPlayer?.player_id != null ? String(coachSelectedPlayer.player_id) : null) : resolveViewerPlayerIdFromUser(user)}
          viewUserId={!isPlayerUi ? (coachSelectedPlayer?.user_id != null ? String(coachSelectedPlayer.user_id) : (coachSelectedPlayer?.id != null ? String(coachSelectedPlayer.id) : null)) : (user?.user_id ? String(user.user_id) : null)}
          localeCode={localeCode}
          viewerParticipatedInSession={viewerParticipatedInSelectedSession}
          onExerciseCatalogChanged={(data) => {
            setPlayerSelectedExerciseForResults((prev) => {
              if (!prev || !data?.te_id || String(prev.te_id) !== String(data.te_id)) {
                return prev;
              }
              if (data.scope === "all" && data.exercise) {
                return {
                  ...prev,
                  ex_id: data.ex_id || data.exercise.ex_id || prev.ex_id,
                  ...data.exercise,
                };
              }
              const pid = data.player_id ? String(data.player_id) : null;
              if (!pid || !data.exercise) return prev;
              return {
                ...prev,
                player_ex_id_overrides: {
                  ...(prev.player_ex_id_overrides || {}),
                  [pid]: data.ex_id,
                },
                player_ex_override_details: {
                  ...(prev.player_ex_override_details || {}),
                  [pid]: data.exercise,
                },
              };
            });
          }}
          onAutoevalUpdated={({ te_id, autoEval }) => {
            window.dispatchEvent(
              new CustomEvent("smarttt-autoeval-updated", {
                detail: {
                  te_id,
                  autoEval,
                  ts_id: playerSelectedSessionForResults?.ts_id,
                },
              })
            );
          }}
        />
      )}
    </div>
  );
};
